import { useActorRef, useSelector } from '@xstate/react';
import { useEffect, useRef, type ReactNode } from 'react';
import type { SnapshotFrom } from 'xstate';
import { Button } from '~/components/buttons';
import {
  initialInquiryMachine,
  type InquirySource,
} from '~/features/market/initialInquiry.machine';
import { ModalDialog } from '~/features/vault/documents/ModalDialog';
import type { JobResponse } from '~/types/hlx/jobResponse';
import { ExistingInquiryNotice } from './ExistingInquiryNotice';
import { MessageComposer } from './MessageComposer';

type Snapshot = SnapshotFrom<typeof initialInquiryMachine>;
const selectSource = (s: Snapshot) => s.context.source;
const selectDraft = (s: Snapshot) => s.context.draft;
const selectOpen = (s: Snapshot) => s.context.open;
const selectUncertain = (s: Snapshot) => s.context.uncertain;
const selectError = (s: Snapshot) => s.context.error;
const selectResult = (s: Snapshot) => s.context.result;
const selectBusy = (s: Snapshot) => s.matches('sending');
const selectExistingResponse = (s: Snapshot) => s.context.existingResponse;

type Props = {
  viewerScopeKey: string;
  onStarted: (response: JobResponse) => void;
  children: (controls: {
    openInquiry: (source: InquirySource) => void;
    inquiryBusy: boolean;
  }) => ReactNode;
};

export const InitialInquiryFlow = (props: Props) => (
  <ScopedFlow key={props.viewerScopeKey} {...props} />
);

const ScopedFlow = ({ onStarted, children }: Props) => {
  const actor = useActorRef(initialInquiryMachine);
  const source = useSelector(actor, selectSource);
  const draft = useSelector(actor, selectDraft);
  const open = useSelector(actor, selectOpen);
  const uncertain = useSelector(actor, selectUncertain);
  const error = useSelector(actor, selectError);
  const result = useSelector(actor, selectResult);
  const busy = useSelector(actor, selectBusy);
  const existingResponse = useSelector(actor, selectExistingResponse);
  const callback = useRef(onStarted);
  const notified = useRef<JobResponse | null>(null);
  useEffect(() => {
    callback.current = onStarted;
  }, [onStarted]);
  useEffect(() => {
    if (result && result !== notified.current) {
      notified.current = result;
      callback.current(result);
    }
  }, [result]);
  return (
    <>
      {children({
        openInquiry: (next) => actor.send({ type: 'OPEN', source: next }),
        inquiryBusy: busy || uncertain,
      })}
      {uncertain && !open && source && (
        <div className="m-4 space-y-2 rounded-md border border-grey-900 p-3">
          <p role="status">An inquiry for {source.title} has an unconfirmed outcome.</p>
          <Button
            type="button"
            label="Review original inquiry"
            onClick={() => actor.send({ type: 'OPEN', source })}
          />
        </div>
      )}
      <ModalDialog
        open={open}
        title="Message about this listing"
        closeDisabled={busy}
        onClose={() => actor.send({ type: 'CLOSE' })}
        xButton
      >
        <p className="body-text-sb">{source?.title}</p>
        <p className="mt-2 text-sm">
          Sending an inquiry does not apply, submit an offer, or reserve availability. Identities
          stay masked until the response's unmasking process is completed.
        </p>
        <p role="status" aria-live="polite" className="min-h-10 py-2 text-sm">
          {error}
        </p>
        <MessageComposer
          value={draft}
          disabled={busy || uncertain}
          sending={busy}
          sendDisabled={!!existingResponse}
          masked
          onChange={(value) => actor.send({ type: 'EDIT', value })}
          onSend={() => actor.send({ type: 'SEND' })}
          label="Message"
          sendLabel="Send inquiry"
          inputTag="initial-inquiry-message"
          sendTag="initial-inquiry-send"
        />
        {existingResponse && <ExistingInquiryNotice response={existingResponse} />}
        {uncertain && (
          <div className="flex flex-col gap-3 p-4">
            <Button
              type="button"
              label="Retry original inquiry"
              disabled={busy}
              onClick={() => actor.send({ type: 'RETRY' })}
            />
            <p className="text-sm">
              Discarding this local retry does not undo a message that may already have been sent.
              Check your conversations before starting another inquiry.
            </p>
            <Button
              type="button"
              label="Discard local retry"
              colorVariant="outline"
              disabled={busy}
              onClick={() => actor.send({ type: 'DISCARD_RETRY' })}
            />
          </div>
        )}
      </ModalDialog>
    </>
  );
};
